import React, { useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useDialog } from "../../../DialogContext";
import { backend_api } from "../../../../static/server";
import { LoadingButton } from "@mui/lab";
import {
  getAllAccounts,
  getAllStatement,
  UpdateTransaction,
} from "../../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import { toast } from "react-toastify";
import axios from "axios";
import DrawerTemplate from "../../Cases/DrawerTemplate";
import ContactApiField from "../../Settings/FieldsUi/ContactApiField";
import MatterApiField from "../../Settings/FieldsUi/MatterApiField";
import { getCurrencySymbol } from "../../../../pages/LawyerPages/Settings/CurrencySetForm";
import { buildCurrencyOptions } from "../../../../utils/currencyConfig";
import { isDevMode } from "../../../Auth/SubscriptionContext";

const NewTransactionForm = ({
  accountId,
  accountType,
  TransactionId,
  ClientAccountId,
  isEditMode = false,
}) => {
  const { showDialog, closeDialog } = useDialog();
  const dispatch = useDispatch();
  const {
    token,
    defaultCurrency,
    UserCurrencyList = [],
  } = useSelector((data) => data.user);
  const { AllAccounts } = useSelector((state) => state.account);
  const { allStatement } = useSelector((state) => state.account);
  const previousClientIdRef = useRef(null);

  useEffect(() => {
    dispatch(
      getAllAccounts({ url: `${backend_api}bank/accounts/`, token: token }),
    );
  }, [dispatch, token]);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      account: "",
      amount: "",
      date: "",
      source_destination: "",
      payer_payee: "",
      currency: "",
      exchange_rate: "",
      transaction_type: "",
      check_reference_no: "",
      status: "",
      description: "",
    },
  });

  const selectedAccountId = watch("account");
  const selectedCurrencyValue = watch("currency");
  const selectedClientValue = watch("client");
  const selectedClientId =
    typeof selectedClientValue === "object"
      ? selectedClientValue?.id
      : selectedClientValue;

  const selectedAccount = useMemo(
    () =>
      AllAccounts?.find((account) => account.id === selectedAccountId) || null,
    [AllAccounts, selectedAccountId],
  );

  const currencyOptions = useMemo(
    () =>
      buildCurrencyOptions({
        userCurrencies: UserCurrencyList,
        includeValue:
          selectedAccount?.currency ||
          selectedCurrencyValue ||
          defaultCurrency ||
          "",
      }),
    [
      UserCurrencyList,
      selectedAccount?.currency,
      selectedCurrencyValue,
      defaultCurrency,
    ],
  );

  const selectedCurrency =
    selectedAccount?.currency ||
    selectedCurrencyValue ||
    defaultCurrency ||
    "USD";
  const currencySymbol =
    getCurrencySymbol(selectedCurrency) || selectedCurrency;

  // edit more use two use effect because it set the value of input after the dialog opens for reduse the time of set value in inputs
  useEffect(() => {
    if (isEditMode) {
      dispatch(
        getAllStatement({
          url: `${backend_api}bank/transactions/${TransactionId}/`,
          token: token,
        }),
      );
    }
  }, [isEditMode, TransactionId, dispatch, token]);

  useEffect(() => {
    if (isEditMode && allStatement && Object.keys(allStatement).length > 0) {
      Object.keys(allStatement).forEach((key) => {
        setValue(key, allStatement[key], { shouldDirty: true });
      });
    }
  }, [isEditMode, allStatement, setValue]);

  useEffect(() => {
    if (!AllAccounts || !accountId) return;
    const selectedAccount =
      AllAccounts.find((acc) => acc.id === Number(accountId)) || null;
    setValue("account", selectedAccount?.id);
    if (selectedAccount?.currency) {
      setValue("currency", selectedAccount.currency, { shouldValidate: true });
    }
    if (!isEditMode) {
      const today = dayjs().format("YYYY-MM-DD");
      setValue("date", today, { shouldValidate: true });
    }

  }, [
    accountId,
    AllAccounts,
    isEditMode,
    setValue,
  ]);

  useEffect(() => {
    if (ClientAccountId && accountType === "client fund") {
      setValue("client", ClientAccountId, { shouldValidate: true });
    }
  }, [ClientAccountId, accountType, setValue]);

  useEffect(() => {
    if (!selectedAccount?.currency) return;

    if (selectedCurrencyValue !== selectedAccount.currency) {
      setValue("currency", selectedAccount.currency, { shouldValidate: true });
    }
  }, [selectedAccount, selectedCurrencyValue, setValue]);

  useEffect(() => {
    if (accountType !== "client fund") return;

    const nextClientId = selectedClientId || null;
    const previousClientId = previousClientIdRef.current;

    if (previousClientId !== null && previousClientId !== nextClientId) {
      setValue("matter", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    previousClientIdRef.current = nextClientId;
  }, [accountType, selectedClientId, setValue]);

  const onSubmit = async (data) => {
    try {
      let response;
      if (isEditMode) {
        response = await dispatch(
          UpdateTransaction({
            url: `${backend_api}bank/transactions/${TransactionId}/`,
            body: data,
            token: token,
          }),
        );
        toast.success("Transaction updated successfully ");
      } else {
        response = await axios.post(`${backend_api}bank/transactions/`, data, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        toast.success("Transaction recorded successfully ");
      }

      reset();
      closeDialog();
      dispatch(
        getAllStatement({
          url: `${backend_api}bank/transactions/?account=${accountId}`,
          token: token,
        }),
      );
      return response;
    } catch (error) {
      toast.error(
        isEditMode
          ? "Failed to update transaction "
          : "Failed to record transaction ",
      );
    }
  };

  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
            }}
          >
            {selectedAccount?.currency && isDevMode && (
              <Alert severity="info" sx={{ alignItems: "center" }}>
                Transactions in this form use the selected account currency:{" "}
                <strong>{selectedAccount.currency}</strong>.
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="transaction_type"
                control={control}
                rules={{ required: "Transaction type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl size="small" fullWidth error={!!error}>
                    <InputLabel
                      id="transaction_type-label"
                      sx={{ bgcolor: "#fff", px: "0.3rem" }}
                    >
                      <span>
                        Transaction Type
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    </InputLabel>
                    <Select {...field} labelId="transaction_type-label">
                      <MenuItem value="deposit">Deposit</MenuItem>
                      <MenuItem value="withdrawal">Withdrawal</MenuItem>
                    </Select>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
              <Controller
                name="currency"
                control={control}
                rules={{ required: "Currency is required" }}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={currencyOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      value={
                        currencyOptions.find(
                          (option) => option?.value === field?.value,
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue?.value : null)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      disabled={Boolean(selectedAccount?.currency)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <span>
                              Currency<span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          size="small"
                          fullWidth
                          error={!!errors?.currency}
                          helperText={
                            errors?.currency?.message ||
                            (selectedAccount?.currency
                              ? "Currency follows the selected account."
                              : "")
                          }
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="amount"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={
                      <span>
                        Amount<span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    error={errors?.amount}
                    helperText={errors?.amount?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: "6px",
                              backgroundColor: "white",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {currencySymbol}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="exchange_rate" //this input use for the set the exchange rate of two diff currency rate inr to usd
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Exchange Rate"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="date"
                control={control}
                rules={{ required: "Date is required" }}
                defaultValue={dayjs().format("YYYY-MM-DD")}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      if (newValue && dayjs(newValue).isValid()) {
                        field.onChange(dayjs(newValue).format("YYYY-MM-DD"));
                      } else {
                        field.onChange(null);
                      }
                    }}
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: {
                        label: (
                          <span>
                            Date <span style={{ color: "red" }}>*</span>
                          </span>
                        ),
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    format="MM/DD/YYYY"
                  />
                )}
              />
              <Controller
                name="account"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <FormControl fullWidth error={errors?.account}>
                    <Autocomplete
                      disablePortal
                      options={AllAccounts || []}
                      getOptionLabel={(option) => option.account_name}
                      value={
                        AllAccounts?.find((acc) => acc.id === field.value) ||
                        null
                      } // Fix here
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue.id : null)
                      }
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1">
                            {option.account_name}
                          </Typography>
                          <Box>
                            {option.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <span>
                              Account<span style={{ color: "red" }}>*</span>
                            </span>
                          }
                          size="small"
                          error={!!errors?.account}
                          helperText={errors?.account?.message}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Box>

            {accountType === "client fund" && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <ContactApiField
                  name="client"
                  label="Client"
                  control={control}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}contact/retrieve/`}
                  isRequired={accountType === "client fund"}
                  editData={ClientAccountId ? { id: ClientAccountId } : null}
                  returnFullData={false}
                />
                <Controller
                  name="matter"
                  control={control}
                  render={({ field }) => (
                    <MatterApiField
                      name="matter"
                      label="Matter"
                      value={field.value}
                      onChange={field.onChange}
                      errors={errors}
                      token={token}
                      apiUrl={
                        selectedClientId
                          ? `${backend_api}case/register/?case_related_person=${selectedClientId}`
                          : `${backend_api}case/register/`
                      }
                      isRequired={accountType === "client fund"}
                      placeholder={
                        selectedClientId
                          ? "Search or select matter"
                          : "Select client first"
                      }
                      disabled={!selectedClientId}
                      AddMatterFunc={({ onCreated }) =>
                        showDialog(
                          <DrawerTemplate onMatterCreated={onCreated} />,
                          {
                            dialogTitle: "Select Template",
                          },
                        )
                      }
                    />
                  )}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="source_destination"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Source/Destination"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="payer_payee"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payer/Payee"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field }) => (
                  <FormControl size="small" fullWidth error={errors?.status}>
                    <InputLabel
                      id="status-label"
                      sx={{ bgcolor: "#fff", px: "0.3rem" }}
                    >
                      <span>
                        Status<span style={{ color: "red" }}>*</span>
                      </span>
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="status-label"
                      defaultValue="pending"
                    >
                      <MenuItem value={"pending"}>Pending</MenuItem>
                      <MenuItem value={"completed"}>Completed</MenuItem>
                    </Select>
                    {errors?.between_to && (
                      <FormHelperText>
                        {errors.between_to.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="check_reference_no"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Check or reference no."
                    size="small"
                  />
                )}
              />
            </Box>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={1.5}
                  fullWidth
                  label="Description"
                />
              )}
            />
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => closeDialog()}
            >
              Cancel
            </Button>
            <LoadingButton
              //   loading={submitLoading}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              {isEditMode ? "Edit Transaction" : "Recode Transaction"}
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default NewTransactionForm;
